import { BaseModal, IconButton, StrokeIcon } from '@acsbe/ui';
import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { useDomainSelection, useMerchantSession } from '../../context';
import { useAnalytics } from '../../services/analytics';
import { REVIEW_PROMPT_SHOWN } from '../../services/analytics/event-names';
import { NegativePath } from './NegativePath';
import { PositivePath } from './PositivePath';
import { SentimentPrompt } from './SentimentPrompt';
import type { ReviewState } from './type';

export const ReviewModal = () => {
  const [reviewState, setReviewState] = useState<ReviewState>('prompt');
  const { postFeedback, accountId } = useDomainSelection();
  const { userFeedback, accessibeUser, merchantDetails } = useMerchantSession();
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.mixpanel.defaultProperties.accountId = accountId;
    analytics.mixpanel.defaultProperties.distinct_id = accessibeUser?.userId;
    analytics.mixpanel.defaultProperties.$user_id = accessibeUser?.userId;

    analytics.mixpanel.trackEvent(REVIEW_PROMPT_SHOWN, {
      trigger: userFeedback?.cmsFeedbackTriggerType,
      pluginSource: merchantDetails?.source,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const element = window.document.body;
    element.classList.add('remove-blur-background');
    return () => element.classList.remove('remove-blur-background');
  }, []);

  const onClose = () => {
    postFeedback();
  };

  return (
    <StyledModal open={true} closeButton>
      <CloseButton
        aria-label="Close"
        data-mixpanel="close-review-modal"
        data-mixpanel-props={JSON.stringify({
          sentiment: userFeedback?.sentiment ?? 'dismissed',
        })}
        padding={1.5}
        scale="alternative"
        variant="solid"
        radius="round"
        onClick={() => onClose()}
      >
        <StrokeIcon name="Close" size="lg" />
      </CloseButton>
      {reviewState === 'prompt' && (
        <SentimentPrompt
          setReview={(review: ReviewState) => setReviewState(review)}
        />
      )}
      {reviewState === 'positive' && <PositivePath />}
      {reviewState === 'negative' && <NegativePath />}
    </StyledModal>
  );
};

const StyledModal = styled(BaseModal)`
  max-width: 520px;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  inset-block-start: 5px;
  inset-inline-end: 5px;
  background-color: transparent;
  color: #394c61;
`;
